import { useEffect, useMemo, useState } from "react";
import { globalSearch, TSearchDealItem, TSearchLeadItem, TSearchRecordingItem } from "@/services/search.service";
import { GROUP_LIMIT, SearchItem, SearchPage } from "./types";

type Group<T> = { items: T[]; total: number };
const EMPTY = { items: [], total: 0 };

/**
 * Debounced grouped search (GET /search) plus client-side page matches,
 * flattened into the keyboard-navigable `items` list. Resets on every open.
 */
export function useSearchResults(open: boolean, pages: SearchPage[]) {
  const [query, setQuery] = useState("");
  const [leads, setLeads] = useState<Group<TSearchLeadItem>>(EMPTY);
  const [deals, setDeals] = useState<Group<TSearchDealItem>>(EMPTY);
  const [recordings, setRecordings] = useState<Group<TSearchRecordingItem>>(EMPTY);
  const [loading, setLoading] = useState(false);

  const trimmed = query.trim();
  const hasQuery = trimmed.length > 0;

  const reset = () => {
    setLeads(EMPTY);
    setDeals(EMPTY);
    setRecordings(EMPTY);
  };

  useEffect(() => {
    if (!open) return;
    setQuery("");
    reset();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (trimmed.length < 2) {
      reset();
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await globalSearch(trimmed, GROUP_LIMIT);
        setLeads({ items: response.leads?.items?.filter((i) => i.type === "lead") ?? [], total: response.leads?.total ?? 0 });
        setDeals({ items: response.deals?.items?.filter((i) => i.type === "deal") ?? [], total: response.deals?.total ?? 0 });
        setRecordings({
          items: response.recordings?.items?.filter((i) => i.type === "recording") ?? [],
          total: response.recordings?.total ?? 0,
        });
      } catch {
        reset();
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [trimmed, open]);

  const pageMatches = useMemo(
    () => (hasQuery ? pages.filter((p) => p.title.toLowerCase().includes(trimmed.toLowerCase())) : pages),
    [hasQuery, pages, trimmed]
  );

  const items = useMemo<SearchItem[]>(
    () => [
      ...pageMatches.map((page): SearchItem => ({ kind: "page", page })),
      ...(hasQuery ? leads.items.map((lead): SearchItem => ({ kind: "lead", lead })) : []),
      ...(hasQuery ? deals.items.map((deal): SearchItem => ({ kind: "deal", deal })) : []),
      ...(hasQuery ? recordings.items.map((recording): SearchItem => ({ kind: "recording", recording })) : []),
    ],
    [pageMatches, hasQuery, leads, deals, recordings]
  );

  return { query, setQuery, trimmed, hasQuery, loading, items, pageMatches, leads, deals, recordings };
}
